const FAKE_PLAYER_NAMES = [
  'Alex Rivera',
  'Jordan Lee',
  'Sam Patel',
  'Taylor Brooks',
  'Morgan Chen',
  'Casey Wright',
  'Riley Jones',
  'Jamie Fox',
  'Avery Kim',
  'Quinn Davis',
];

const randomInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

export const PRACTICE_PARTICIPANT_ID = 'practice_user';
export const PRACTICE_PARTICIPANT_COUNT = 24;

export const DEFAULT_PRACTICE_POINTS_CONFIG = {
  basePoints: 1000,
  speedBonusMultiplier: 2,
  maxPointsPerQuestion: 3000,
};

/** Demo leaderboard scoring rules (display) */
export const DEMO_SCORING_RULES = {
  basePoints: 1000,
  baseTimeSeconds: 10,
  difficulty: 'Easy',
  tiers: [
    { label: 'Fast Correct (≤5s)', points: 3000, variant: 'gain' },
    { label: 'On-time Correct (≤10s)', points: 1500, variant: 'gain' },
    { label: 'Late Correct', points: 500, variant: 'gain' },
    { label: 'Wrong Answer', points: -1000, variant: 'loss' },
    { label: 'Penalty', points: -2000, variant: 'loss' },
  ],
};

export function formatPoints(value) {
  const n = Number(value) || 0;
  return n.toLocaleString();
}

export function getMaxPointsPerQuizlet(pointsConfig, questionsCount = 5) {
  const cfg = pointsConfig || DEFAULT_PRACTICE_POINTS_CONFIG;
  const perQuestion = cfg.maxPointsPerQuestion
    || Math.round((cfg.basePoints || 1000) * (1 + (cfg.speedBonusMultiplier || 2)));
  return perQuestion * questionsCount;
}

export function createFakeLeaderboard(userDisplayName, userScore = 0) {
  const bots = FAKE_PLAYER_NAMES.slice(0, 9).map((name, index) => ({
    participant_id: `practice_bot_${index}`,
    guest_name: name,
    fname: name.split(' ')[0],
    lname: name.split(' ').slice(1).join(' '),
    current_score: randomInt(800, 4500),
    current_position: index + 1,
  }));

  const userEntry = {
    participant_id: PRACTICE_PARTICIPANT_ID,
    guest_name: userDisplayName,
    fname: userDisplayName,
    lname: '',
    current_score: userScore,
    current_position: bots.length + 1,
    last_points_gain: 0,
    is_user: true,
  };

  return sortLeaderboard([...bots, userEntry]);
}

export function getLeaderboardDisplayName(entry) {
  const guest = (entry?.guest_name || '').trim();
  if (guest) return guest;
  const fname = (entry?.fname || '').trim();
  const lname = (entry?.lname || '').trim();
  const combined = `${fname} ${lname}`.trim();
  return combined || 'Player';
}

export function sortLeaderboard(entries) {
  return [...entries]
    .sort((a, b) => (b.current_score || 0) - (a.current_score || 0))
    .map((entry, index) => ({
      ...entry,
      current_position: index + 1,
    }));
}

export function updateFakeLeaderboardAfterQuizlet(leaderboard, quizletScore) {
  const updated = leaderboard.map((entry) => {
    if (entry.participant_id === PRACTICE_PARTICIPANT_ID) {
      return {
        ...entry,
        current_score: (entry.current_score || 0) + (quizletScore || 0),
        last_points_gain: quizletScore || 0,
      };
    }
    const bump = randomInt(0, Math.max(200, Math.round((quizletScore || 0) * 0.4)));
    return {
      ...entry,
      current_score: (entry.current_score || 0) + bump,
      last_points_gain: bump,
    };
  });
  return sortLeaderboard(updated);
}

export function getUserRank(leaderboard) {
  const index = leaderboard.findIndex((e) => e.participant_id === PRACTICE_PARTICIPANT_ID);
  return index >= 0 ? index + 1 : null;
}

export function getFakeQuizletRank(quizletScore, maxPoints) {
  if (!maxPoints) return randomInt(2, 6);
  const ratio = Math.min(1, Math.max(0, quizletScore / maxPoints));
  if (ratio >= 0.85) return 1;
  if (ratio >= 0.65) return randomInt(1, 3);
  if (ratio >= 0.4) return randomInt(3, 6);
  return randomInt(5, 9);
}

export function buildPracticePushedQuizlets(quizlets, completedIndices = new Set(), runningIndex = null) {
  return quizlets.map((quizlet, index) => {
    const pushedAt = new Date(Date.now() - (quizlets.length - index) * 60000).toISOString();
    const id = `${quizlet.song?.songs_master_id || 'song'}_${pushedAt}`;
    let status = 'expired';
    if (completedIndices.has(index)) status = 'completed';
    else if (runningIndex === index) status = 'running';

    return {
      id,
      song: quizlet.song || {},
      pushed_at: pushedAt,
      delivery_time: pushedAt,
      expire_at: null,
      status,
      userPlayed: completedIndices.has(index),
    };
  }).reverse();
}

export function buildPracticeQuizletStats(quizletId, results, quizletRank) {
  return {
    [quizletId]: {
      quizlet_id: quizletId,
      questions_count: results?.totalQuestions || results?.questionResults?.length || 0,
      total_points: results?.totalScore || 0,
      quizlet_rank: quizletRank,
      time_taken: results?.timeTaken ? Math.round(results.timeTaken) : null,
      completed_at: new Date().toISOString(),
    },
  };
}

export function getUserLeaderboardEntry(leaderboard) {
  return leaderboard.find((e) => e.participant_id === PRACTICE_PARTICIPANT_ID) || null;
}

export function getTopScore(leaderboard) {
  if (!leaderboard?.length) return 0;
  return Math.max(...leaderboard.map((e) => e.current_score || 0), 0);
}
